import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, MessageCircle, MapPin, Phone, Mail, ArrowUp } from 'lucide-react';
import { SiTiktok } from 'react-icons/si';
import { useLocation, useNavigate } from 'react-router-dom';


const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href: string) => {
    if (location.pathname === '/all-gallery') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Shop', href: '#shop' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    'Autobody Repairs',
    'Paint & Bodywork',
    'Parts Sales',
    'Complete Restorations',
    'Matric Dance Hire',
    'Rust Treatment'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-32 h-32 mini-float">
          <svg 
            width="128" 
            height="128" 
            viewBox="0 0 120 60" 
            className="w-full h-full opacity-60"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse cx="60" cy="45" rx="45" ry="12" fill="#DC2626"/>
            <ellipse cx="60" cy="35" rx="25" ry="8" fill="#FFFFFF"/>
            <circle cx="35" cy="52" r="8" fill="#374151"/>
            <circle cx="85" cy="52" r="8" fill="#374151"/>
          </svg>
        </div>
        <div className="absolute bottom-10 right-10 w-24 h-24 sparkle">
          <svg 
            width="96" 
            height="96" 
            viewBox="0 0 120 60" 
            className="w-full h-full opacity-60"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse cx="60" cy="45" rx="45" ry="12" fill="#16A34A"/>
            <ellipse cx="60" cy="35" rx="25" ry="8" fill="#FFFFFF"/>
            <circle cx="35" cy="52" r="8" fill="#374151"/>
            <circle cx="85" cy="52" r="8" fill="#374151"/>
          </svg>
        </div>
        {/* Driving Mini across footer */}
        <div 
          style={{
            animationDelay: '5s',
            animation: 'driveMini 20s linear infinite',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10
          }}
        >
          <svg 
            width="120" 
            height="60" 
            viewBox="0 0 120 60" 
            className="drop-shadow-lg"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse cx="60" cy="45" rx="45" ry="12" fill="#DC2626" stroke="#B91C1C" strokeWidth="1"/>
            <ellipse cx="60" cy="35" rx="25" ry="8" fill="#FFFFFF" stroke="#D1D5DB" strokeWidth="1"/>
            <circle cx="18" cy="40" r="3" fill="#FFF"/>
            <circle cx="102" cy="40" r="2.5" fill="#EF4444"/>
            <circle cx="35" cy="52" r="8" fill="#374151"/>
            <circle cx="85" cy="52" r="8" fill="#374151"/>
          </svg>
        </div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="section-padding border-b border-gray-800">
          <div className="container-custom">
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={{
                animate: {
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
            >
              {/* Company Info */}
              <motion.div variants={fadeInUp}>
                <div className="flex items-center space-x-2 mb-6">
                  <div className="relative w-14 h-10 flex items-center justify-center">
                    <span className="absolute left-0 top-0 w-10 h-10 bg-mini-red rounded-full flex items-center justify-center z-10">
                        <span className="text-white font-bold text-lg">GC</span>
                    </span>
                    {/* Animated Mini SVG */}
                    <motion.svg
                      width="44" height="22" viewBox="0 0 120 60"
                      className="absolute left-8 top-1 bounce-gentle drop-shadow"
                      xmlns="http://www.w3.org/2000/svg"
                      initial={{ y: 0 }}
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <ellipse cx="60" cy="45" rx="45" ry="12" fill="#D32F2F" stroke="#B91C1C" strokeWidth="1"/>
                      <ellipse cx="60" cy="35" rx="25" ry="8" fill="#FFFFFF" stroke="#D1D5DB" strokeWidth="1"/>
                      <circle cx="18" cy="40" r="3" fill="#FFF"/>
                      <circle cx="102" cy="40" r="2.5" fill="#EF4444"/>
                      <circle cx="35" cy="52" r="8" fill="#374151"/>
                      <circle cx="85" cy="52" r="8" fill="#374151"/>
                    </motion.svg>
                  </div>
                  <div>
                      <h3 className="text-xl font-heading font-bold">Gerrie's Classic Minis</h3>
                    <p className="text-sm text-gray-400">Keeping Classic Minis Alive</p>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Specialists in Mini restoration, repairs, and authentic parts for classic Minis from the 60s, 70s, 80s & 90s.
                </p>

                {/* Social Media */}
                <div className="flex space-x-4">
                  <motion.a
                    href="https://www.facebook.com/profile.php?id=61576832646515"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-mini-red rounded-lg flex items-center justify-center transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </motion.a>
                  <motion.a
                    href="#"
                    className="w-10 h-10 bg-gray-800 hover:bg-pink-500 rounded-lg flex items-center justify-center transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="TikTok"
                  >
                    {(SiTiktok as any)({ size: 20 })}
                  </motion.a>
                </div>
              </motion.div>

              {/* Quick Links */}
              <motion.div variants={fadeInUp}>
                <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
                <ul className="space-y-3">
                  {quickLinks.map((link) => (
                    <li key={link.name}>
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className="text-gray-300 hover:text-mini-red transition-colors duration-200 flex items-center group"
                      >
                        <span className="transform group-hover:translate-x-1 transition-transform duration-200">
                          {link.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Services */}
              <motion.div variants={fadeInUp}>
                <h4 className="text-lg font-semibold mb-6">Our Services</h4>
                <ul className="space-y-3">
                  {services.map((service) => (
                    <li key={service} className="text-gray-300 flex items-center">
                      <div className="w-1.5 h-1.5 bg-mini-red rounded-full mr-3 flex-shrink-0" />
                      {service}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Contact Info */}
              <motion.div variants={fadeInUp}>
                <h4 className="text-lg font-semibold mb-6">Contact Info</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin size={18} className="text-mini-red mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-300 text-sm">
                        461 Jasmyn Street Cnr, Dykor St<br />
                        Silverton, Pretoria, 0184
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Phone size={18} className="text-mini-red flex-shrink-0" />
                    <a 
                      href="tel:+27729981365"
                      className="text-gray-300 hover:text-mini-red transition-colors text-sm"
                      aria-label="Call Gerrie's Classic Minis"
                    >
                      +27 72 998 1365
                    </a>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Mail size={18} className="text-mini-red flex-shrink-0" />
                    <a 
                      href="mailto:mike@silvertoncc.co.za"
                      className="text-gray-300 hover:text-mini-red transition-colors text-sm"
                      aria-label="Email Gerrie's Classic Minis"
                    >
                      mike@silvertoncc.co.za
                    </a>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                  <h5 className="font-semibold text-sm mb-2">Business Hours</h5>
                  <div className="text-xs text-gray-400 space-y-1">
                    <div className="flex justify-between">
                      <span>Mon - Fri:</span>
                      <span>8:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Weekends:</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-6">
          <div className="container-custom">
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <motion.div 
                className="flex items-center space-x-1 text-gray-400 text-sm mb-4 sm:mb-0"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span>
                  © {currentYear} Gerrie's Classic Minis. Built by{' '}
                  <a href="https://aethertech.co.za" target="_blank" rel="noopener noreferrer" className="text-mini-red hover:underline">Aether Tech</a>
                  .
                </span>
              </motion.div>

              <motion.div 
                className="flex items-center space-x-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Animated Social Icons */}
                <motion.a
                  href="https://www.facebook.com/profile.php?id=61576832646515"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200"
                  whileHover={{ scale: 1.2, rotate: -10 }}
                  whileTap={{ scale: 0.95, rotate: 5 }}
                  aria-label="Facebook"
                >
                  <Facebook size={22} />
                </motion.a>
                <motion.a
                  href="https://www.tiktok.com/@miniclassicsgarage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-black transition-colors duration-200"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  whileTap={{ scale: 0.95, rotate: -5 }}
                  aria-label="TikTok"
                >
                  {(SiTiktok as any)({ size: 22 })}
                </motion.a>
                <button
                  onClick={scrollToTop}
                  className="flex items-center space-x-2 text-gray-400 hover:text-mini-red transition-colors duration-200 text-sm group"
                  aria-label="Back to Top"
                >
                  <span>Back to Top</span>
                  <ArrowUp size={16} className="transform group-hover:-translate-y-1 transition-transform duration-200" />
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Back to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 bg-mini-red hover:bg-red-700 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-colors duration-300"
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Back to Top"
      >
        <ArrowUp size={20} />
      </motion.button>
    </footer>
  );
};

export default Footer;
